import { PROPERTY_GRID_SUBJECT_PROPERTY } from '@aloft/toolkit-shared';
import { Flex, Typography, colors } from '@aloft/toolkit-sui';

import { useWatch } from 'react-hook-form';

import { PROPERTY_GRID_ROW_HEIGHT } from '../consts.ts';
import { readOnlyFieldProps } from './styles.ts';
import type { PropertyGridFieldProps } from './types.ts';
import { resolveFieldName } from './utils.tsx';

const PlaceholderField = ({ colSpan }: { colSpan: number }) => (
  <Typography
    {...readOnlyFieldProps(colSpan)}
    __dangerDoNotUseSx={{
      ...readOnlyFieldProps(colSpan).__dangerDoNotUseSx,
      backgroundColor: colors.surface1,
    }}
  >
    —
  </Typography>
);

export const ReadOnlyDifferenceField = ({
  columnIndex,
  field,
  colSpan = 1,
  control,
  getValues,
}: PropertyGridFieldProps) => {
  const { fieldName: rawFieldName, toNumericValue, unit } = field;

  const propertyId = getValues(`columns.${columnIndex}.propertyId`);
  const isSubject = propertyId === PROPERTY_GRID_SUBJECT_PROPERTY;

  const { propertyDataFieldName: compFieldName } = resolveFieldName(
    rawFieldName,
    columnIndex,
    isSubject,
    false,
  );

  const { propertyDataFieldName: subjectFieldName } = resolveFieldName(
    rawFieldName,
    0,
    true,
    false,
  );

  const compRawValue = useWatch({ control, name: compFieldName });
  const subjectRawValue = useWatch({
    control,
    name: subjectFieldName,
  });

  if (isSubject || !toNumericValue) return <PlaceholderField colSpan={colSpan} />;

  const subjectNum = toNumericValue(subjectRawValue);
  const compNum = toNumericValue(compRawValue);

  if (subjectNum === undefined || compNum === undefined) {
    return <PlaceholderField colSpan={colSpan} />;
  }

  const diff = subjectNum - compNum;
  const unitSuffix = unit ? ` ${unit}` : '';
  const formulaLabel = `${subjectNum} - ${compNum} =`;
  const resultLabel = `${diff}${unitSuffix}`;

  return (
    <Flex
      flexDirection="column"
      justifyContent="center"
      height={PROPERTY_GRID_ROW_HEIGHT}
      __dangerDoNotUseSx={{
        ...readOnlyFieldProps(colSpan).__dangerDoNotUseSx,
        backgroundColor: colors.surface1,
      }}
    >
      <Typography variant="body2" color="textTertiary" __dangerDoNotUseSx={{ fontSize: '10px' }}>
        {formulaLabel}
      </Typography>
      <Typography variant="body1">{resultLabel}</Typography>
    </Flex>
  );
};
